/**
 * Wire types for `/public/checkout/intent`; the generated payment-service client does not know these routes yet.
 * A line carries its amounts only: product names, images, types and trial terms come from the build-time prices.
 */
import type { CheckoutCountry } from '@/lib/checkout/types'

import type {
  CheckoutSessionCreationErrorResponseType,
  GetCheckoutSessionDetailsResponsePaymentMethodType,
} from '@/clients/payment-service'

/**
 * - `OPEN`: needs confirming; also the state after a payment failure that needs no further step.
 * - `CONFIRMING`: a confirm run holds the reference; other confirms and every update are refused.
 * - `CONFIRM_NEEDS_ACTION`: Stripe wants a next action (3DS, Klarna); the browser completes it with the client secret.
 * - `CONFIRM_SUCCESS`: the payment succeeded or was accepted; the order does not exist yet.
 * - `COMPLETED`: the order exists.
 * - `EXPIRED`: closed unpaid, by Stripe's expiry of the subscription or by the sweep; terminal.
 */
export type IntentCheckoutStatus =
  | 'OPEN'
  | 'CONFIRMING'
  | 'CONFIRM_NEEDS_ACTION'
  | 'CONFIRM_SUCCESS'
  | 'COMPLETED'
  | 'EXPIRED'

export type IntentBasketLine = {
  price_lookup_key: string
  quantity: number
}

export type IntentCheckoutLine = {
  price_lookup_key: string
  description: string
  quantity: number
  amount_subtotal: number
  amount_discount: number
  amount_tax: number
  amount_total: number
  billing_interval: string | null
  billing_interval_count: number | null
  /** Set on an intro fee (a trial or the welcome offer): the subscription it starts. */
  renewal: IntentCheckoutRenewal | null
}

export type IntentCheckoutRenewal = {
  price_lookup_key: string
  /** What the first renewal bills, after repeating/forever coupons and exclusive tax; null once confirm raised the invoice. */
  amount: number | null
}

export type IntentCheckoutTaxLine = {
  name: string
  /** A percentage, e.g. `20`; null when Stripe gives no rate. */
  rate: number | null
  inclusive: boolean
  amount: number
}

/** Apple Pay's billing period: `day`, `month` or `year`. */
export type IntentApplePayBilling = {
  amount: number
  label: string
  interval: 'day' | 'month' | 'year'
  interval_count: number
  /** ISO instant; null when the billing starts now. */
  start_date?: string | null
}

export type IntentApplePayRecurring = {
  payment_description: string
  management_url: string
  regular_billing: IntentApplePayBilling
  trial_billing: IntentApplePayBilling | null
}

/**
 * How the checkout takes payment, which payment-service decides with the rule confirm uses:
 * - `payment`: a one-off basket with something due.
 * - `subscription`: a basket that renews, with something due.
 * - `setup`: a basket that renews with nothing due; the card is saved with a SetupIntent.
 * - `none`: a one-off basket with nothing due. No Payment Element is shown and Pay confirms without a ConfirmationToken.
 */
export type ElementsMode = 'payment' | 'subscription' | 'setup' | 'none'

export type IntentStripeElements = {
  mode: ElementsMode
  amount: number
  currency: string
  setup_future_usage: 'off_session' | null
  /** Set for `subscription` and `setup`, except on a quote read after confirm. */
  apple_pay_recurring: IntentApplePayRecurring | null
}

type IntentCheckoutQuoteBase = {
  currency: string
  amount_subtotal: number
  amount_discount: number
  /** The total Stripe bills when `tax_status` is `CALCULATED`; before tax when it is `PENDING_ADDRESS`. */
  amount_total: number
  applied_promotion_code: string | null
  lines: IntentCheckoutLine[]
  /** One row per tax rate; empty while `PENDING_ADDRESS`. */
  tax_lines: IntentCheckoutTaxLine[]
  stripe_elements: IntentStripeElements
}

/**
 * What Stripe will bill; its lines follow the invoice, not the basket. A US checkout with no shipping address yet is
 * `PENDING_ADDRESS`: priced without tax (`amount_tax` null) and never payable.
 */
export type IntentCheckoutQuote = IntentCheckoutQuoteBase &
  ({ tax_status: 'CALCULATED'; amount_tax: number } | { tax_status: 'PENDING_ADDRESS'; amount_tax: null })

export const isCalculatedQuote = (
  quote: IntentCheckoutQuote | null | undefined,
): quote is Extract<IntentCheckoutQuote, { tax_status: 'CALCULATED' }> => quote?.tax_status === 'CALCULATED'

export type IntentCheckoutEligibility = {
  eligible: boolean
  existing_member: boolean
  ineligible_reason: CheckoutSessionCreationErrorResponseType | null
}

export type IntentCheckoutResponse = {
  id: string
  status: IntentCheckoutStatus
  basket: IntentBasketLine[]
  quote: IntentCheckoutQuote | null
  eligibility: IntentCheckoutEligibility | null
  customer_session_client_secret: string | null
  email: string | null
  shipping_address: IntentCheckoutShippingAddress | null
  // The fields below are set once Stripe has the payment.
  payment_method_type?: GetCheckoutSessionDetailsResponsePaymentMethodType
  customer_id?: string | null
  customer_first_name?: string | null
  customer_last_name?: string | null
}

export type CreateIntentCheckoutRequest = {
  basket: IntentBasketLine[]
  country_code: CheckoutCountry
  email?: string | null
  /** Either a Stripe promotion code id (`promo_...`) or the human-readable code. */
  promotion_code?: string | null
  /** Set at create only; omitted when no timer runs. */
  incentive_decision_timer_end?: string | null
}

export type IntentCheckoutShippingAddress = {
  name: string
  line1: string
  line2?: string | null
  city?: string | null
  postal_code?: string | null
  country?: string | null
  state?: string | null
}

/** A field left out (or null) is left untouched. */
export type UpdateIntentCheckoutRequest = {
  basket?: IntentBasketLine[]
  email?: string | null
  promotion_code?: string | null
  /** Takes precedence over `promotion_code`. */
  clear_promotion_code?: boolean
  shipping_address?: IntentCheckoutShippingAddress | null
}

export type ConfirmIntentCheckoutRequest = {
  /** Left out only when nothing is due and nothing renews (a £0 one-off basket); the server refuses a missing one otherwise. */
  confirmation_token_id?: string
  return_url?: string
  /** The basket from the latest response; a confirm against a basket the server no longer holds is refused. */
  basket: IntentBasketLine[]
  /** A guest's committed email; omitted for a signed-in customer. */
  email?: string
}

/** `NEEDS_ACTION` carries the client secret for `stripe.handleNextAction`, after which the client calls success. */
export type ConfirmIntentCheckoutResponse = { code: 'PROCEED' } | { code: 'NEEDS_ACTION'; client_secret: string }

export const INTENT_CONFLICT_CODES = [
  'AMOUNT_CHANGED',
  'CHECKOUT_OUT_OF_DATE',
  'CONFIRM_IN_PROGRESS',
  'ALREADY_CONFIRMED',
  'EXISTING_MEMBER',
  'PAYMENT_NOT_SUCCEEDED',
] as const

export type IntentConflictCode = (typeof INTENT_CONFLICT_CODES)[number]

/** The rejections the form can act on; anything else (401, 403, 404, other 5xx) is rethrown untouched. */
export type IntentCheckoutError =
  | { status: 402; code: 'CARD_DECLINED'; message: string | null; declineCode: string | null }
  | { status: 409; code: IntentConflictCode; message: string | null }
  | { status: 410; code: string | null; message: string | null }
  | { status: 422; code: 'TAX_LOCATION_INVALID'; message: string | null }
  | { status: 422; code: 'INVALID_PROMO_CODE'; message: string | null }
  | { status: 503; code: 'TAX_CALCULATION_FAILED'; message: string | null }
  | { status: 422; code: CheckoutSessionCreationErrorResponseType; message: string | null }
